import { create } from 'zustand';
import { projects } from '../data/projects';
import type { Project } from '../types';

type Page = 'loading' | 'home' | 'about' | 'transitioning';

interface AppState {
  // Page state
  currentPage: Page;
  loadingProgress: number;

  // Project state
  selectedProject: Project | null;
  selectedIndex: number | null;

  // Additional state
  isInitialAnimationComplete: boolean;
  hasScrolled: boolean;

  // New state
  nearestProjectIndex: number;

  // New state
  scrollOffset: number;

  // New state
  galleryScrollOffset: number;
  // New state
  isTransitionComplete: boolean;

  // Actions
  setCurrentPage: (page: Page) => void;
  setLoadingProgress: (progress: number) => void;
  setSelected: (project: Project | null, index: number | null) => void;
  setInitialAnimationComplete: (value: boolean) => void;
  setHasScrolled: (value: boolean) => void;
  setScrollOffset: (offset: number) => void;
  setGalleryScrollOffset: (offset: number) => void;
  setTransitionComplete: (complete: boolean) => void;
}

export const useAppStore = create<AppState>(set => ({
  // Initial state
  currentPage: 'loading',
  loadingProgress: 0,
  selectedProject: null,
  selectedIndex: null,
  isInitialAnimationComplete: false,
  hasScrolled: false,
  nearestProjectIndex: Math.floor(projects.length / 2),
  scrollOffset: 0,
  galleryScrollOffset: 0,
  isTransitionComplete: false,

  // Actions
  setCurrentPage: page => set({ currentPage: page }),
  setLoadingProgress: progress => set({ loadingProgress: progress }),
  setSelected: (project, index) =>
    set({
      selectedProject: project,
      selectedIndex: index,
    }),
  setInitialAnimationComplete: value =>
    set({ isInitialAnimationComplete: value }),
  setHasScrolled: value => set({ hasScrolled: value }),
  setScrollOffset: offset => set({ scrollOffset: offset }),
  setGalleryScrollOffset: offset => set({ galleryScrollOffset: offset }),
  setTransitionComplete: complete => set({ isTransitionComplete: complete }),
}));

// Selectors pour un accès plus facile aux données
export const useSelectedProject = () =>
  useAppStore(state => ({
    selectedProject: state.selectedProject,
    selectedIndex: state.selectedIndex,
    setSelected: state.setSelected,
  }));

export const usePageState = () =>
  useAppStore(state => ({
    currentPage: state.currentPage,
    loadingProgress: state.loadingProgress,
    setCurrentPage: state.setCurrentPage,
    setLoadingProgress: state.setLoadingProgress,
  }));
